import { LoadingRows } from "../UI/Loading/LoadingRows.tsx";
import { Layout } from "../UI/Layout/Layout.tsx";
import { ScreenTitleBlock } from "../UI/Layout/ScreenTitleBlock.tsx";
import { useAllUsers } from "./useAllUsers.ts";
import { UserTable } from "./UserTable.tsx";
import { DebouncedTextInput } from "../UI/DebouncedTextInput.tsx";
import { Pagination } from "../UI/Pagination.tsx";
import { useCallback, useState } from "react";
import {
  OneOnOneLessonType,
  UserFragmentFragment,
  UserListSortDirection,
  UserListSortField,
} from "../gql/graphql.ts";
import { useToCreateUserScreen } from "./useToCreateUserScreen.ts";
import { useFilterAndPaginationController } from "../QueryFilterUtils/useFilterAndPaginationController.tsx";
import { Card } from "../UI/Card.tsx";
import { Select } from "@normasteaching/norma-ui";
import { UserDetailModalWithActions } from "./UserDetailModalWithActions.tsx";
import { useAllUserGroups } from "../UserGroup/useAllUserGroups.ts";
import { ChartBarIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";
import { IconButton } from "../UI/IconButton.tsx";
import { UserStatisticsScreenPath } from "./Statistics/UserStatisticsScreen.tsx";
import {
  DEFAULT_USER_TABLE_SORT,
  isDefaultUserTableSort,
  toActivityRangesInput,
  UserTableFilters,
  UserTableSort,
} from "./userTableState.ts";
import { useUserActivePlans } from "./useUserActivePlans.ts";

type UserFilter = Partial<{
  search: string;
  hasOneOnOneActive: boolean;
  oneOnOneLessonType: OneOnOneLessonType;
  hasGroupPlanActive: boolean;
  groupUserGroupId: string;
  sort: UserTableSort;
  speakingLevels: UserTableFilters["speakingLevels"];
  activityRanges: UserTableFilters["activityRanges"];
}>;

const SORT_FIELD_LABELS: Record<UserListSortField, string> = {
  [UserListSortField.CreatedAt]: "creation date",
  [UserListSortField.Email]: "email",
  [UserListSortField.SpeakingLevel]: "level",
  [UserListSortField.ReceivedLessonFeedbackCount]: "lesson feedback",
  [UserListSortField.ReceivedProgressCheckFeedbackCount]: "PC feedback",
  [UserListSortField.NoShowCount]: "no show",
  [UserListSortField.ParticipatedProgressCheckCount]: "PC",
  [UserListSortField.ParticipatedSpeakingClassCount]: "RLC",
};

function SortedByNotice({
  sort,
  onReset,
}: {
  sort: UserTableSort;
  onReset: () => void;
}) {
  if (isDefaultUserTableSort(sort)) return null;
  return (
    <span className={"text-sm text-slate-500"}>
      Sorted by {SORT_FIELD_LABELS[sort.sortBy]} (
      {sort.sortDirection === UserListSortDirection.Asc
        ? "ascending"
        : "descending"}
      ) ·{" "}
      <button
        onClick={onReset}
        className={"text-slate-600 underline hover:text-slate-900"}
      >
        Show newest first
      </button>
    </span>
  );
}

function PlanTypeToggle({
  label,
  active,
  onToggle,
}: {
  label: string;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      className={[
        "px-3 py-1.5 rounded-full text-sm font-medium border transition-colors",
        active
          ? "bg-slate-800 text-white border-slate-800"
          : "bg-white text-slate-600 border-slate-300 hover:border-slate-500",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

const usersPerPage = 20;

export const AllUsersScreenPath = "/users";
export function AllUserScreen() {
  const [selectedUser, setSelectedUser] = useState<UserFragmentFragment | null>(
    null,
  );

  const { currentPage, toPage, filter, setFilter } =
    useFilterAndPaginationController<UserFilter>();

  const { userGroups } = useAllUserGroups();
  const { activePlansOf } = useUserActivePlans();
  const userGroupOptions = userGroups.map((g) => ({ name: g.name, id: g.id }));

  const tableSort = filter.sort ?? DEFAULT_USER_TABLE_SORT;
  const tableFilters: UserTableFilters = {
    speakingLevels: filter.speakingLevels ?? [],
    activityRanges: filter.activityRanges ?? {},
  };

  const { users, totalCount, loading } = useAllUsers({
    limit: usersPerPage,
    offset: currentPage * usersPerPage,
    search: filter?.search,
    hasOneOnOneActive: filter?.hasOneOnOneActive,
    oneOnOneLessonType: filter?.oneOnOneLessonType,
    hasGroupPlanActive: filter?.hasGroupPlanActive,
    groupUserGroupId: filter?.groupUserGroupId,
    withSpeakingLevel:
      tableFilters.speakingLevels.length > 0
        ? tableFilters.speakingLevels
        : undefined,
    activityRanges: toActivityRangesInput(tableFilters.activityRanges),
    sortBy: tableSort.sortBy,
    sortDirection: tableSort.sortDirection,
  });

  function changeTableSort(sort: UserTableSort) {
    setFilter({
      ...filter,
      sort: isDefaultUserTableSort(sort) ? undefined : sort,
    });
  }

  function changeTableFilters({
    speakingLevels,
    activityRanges,
  }: UserTableFilters) {
    setFilter({ ...filter, speakingLevels, activityRanges });
  }

  const { toCreateUser } = useToCreateUserScreen();
  const navigate = useNavigate();
  const toUserStatistics = () => navigate(UserStatisticsScreenPath);

  const totalPages = Math.max(1, Math.ceil(totalCount / usersPerPage));

  const onChangeSearchFilter = useCallback(
    (value: string) => {
      setFilter({ ...filter, search: value.trim() || undefined });
    },
    [setFilter, filter],
  );

  function toggleOneOnOne() {
    const nowActive = !filter.hasOneOnOneActive;
    setFilter({
      ...filter,
      hasOneOnOneActive: nowActive || undefined,
      oneOnOneLessonType: nowActive ? filter.oneOnOneLessonType : undefined,
    });
  }

  function toggleGroup() {
    const nowActive = !filter.hasGroupPlanActive;
    setFilter({
      ...filter,
      hasGroupPlanActive: nowActive || undefined,
      groupUserGroupId: nowActive ? filter.groupUserGroupId : undefined,
    });
  }

  const onChangeOneOnOneLessonType = useCallback(
    (value: OneOnOneLessonType | null) => {
      setFilter({ ...filter, oneOnOneLessonType: value ?? undefined });
    },
    [setFilter, filter],
  );

  const onChangeGroupUserGroupId = useCallback(
    (value: string | null) => {
      setFilter({ ...filter, groupUserGroupId: value ?? undefined });
    },
    [setFilter, filter],
  );

  return (
    <Layout>
      {selectedUser && (
        <UserDetailModalWithActions
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      )}
      <Card>
        <ScreenTitleBlock
          title={"Users"}
          action={
            <div className={"flex items-center gap-2 md:gap-3"}>
              <IconButton
                label={"User statistics"}
                icon={ChartBarIcon}
                onClick={toUserStatistics}
              />
              <div className={"hidden md:block"}>
                <PrimaryButton label={"New User"} onClick={toCreateUser} />
              </div>
              <div className={"md:hidden"}>
                <IconButton
                  label={"New User"}
                  icon={PlusIcon}
                  onClick={toCreateUser}
                />
              </div>
            </div>
          }
        />
      </Card>
      <div className={"mt-4"}>
        <Card>
          <div>
            <div className={"mb-1 flex items-center justify-between"}>
              <span className={"text-sm font-medium text-slate-500"}>
                {totalCount} {totalCount === 1 ? "user" : "users"}
              </span>
              <SortedByNotice
                sort={tableSort}
                onReset={() => changeTableSort(DEFAULT_USER_TABLE_SORT)}
              />
            </div>
            <div className={"mb-4 flex flex-col space-y-3"}>
              <DebouncedTextInput
                initialValue={filter.search ?? ""}
                debounceInMs={500}
                onChange={onChangeSearchFilter}
                placeholder={"User ID, email, name or surname"}
              />

              {/* Piano attivo multi-select */}
              <div className={"flex flex-col space-y-2"}>
                <div className={"flex items-center gap-2"}>
                  <span className={"text-sm text-slate-500 font-medium"}>
                    Piano attivo:
                  </span>
                  <PlanTypeToggle
                    label={"1TO1"}
                    active={!!filter.hasOneOnOneActive}
                    onToggle={toggleOneOnOne}
                  />
                  <PlanTypeToggle
                    label={"Group"}
                    active={!!filter.hasGroupPlanActive}
                    onToggle={toggleGroup}
                  />
                </div>

                {/* Sub-filtri */}
                {(filter.hasOneOnOneActive || filter.hasGroupPlanActive) && (
                  <div className={"flex gap-2 pl-2"}>
                    {filter.hasOneOnOneActive && (
                      <div className={"flex-1 max-w-xs"}>
                        <Select<OneOnOneLessonType>
                          placeholder={"Tipo lezione 1TO1"}
                          options={[
                            {
                              name: "General English",
                              id: OneOnOneLessonType.GeneralEnglish,
                            },
                            {
                              name: "Business English",
                              id: OneOnOneLessonType.BusinessEnglish,
                            },
                          ]}
                          value={filter.oneOnOneLessonType}
                          onChange={onChangeOneOnOneLessonType}
                          clearable
                        />
                      </div>
                    )}
                    {filter.hasGroupPlanActive && (
                      <div className={"flex-1 max-w-xs"}>
                        <Select<string>
                          placeholder={"User group"}
                          options={userGroupOptions}
                          value={filter.groupUserGroupId}
                          onChange={onChangeGroupUserGroupId}
                          clearable
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {loading ? (
              <LoadingRows rows={8} />
            ) : (
              <div>
                <UserTable
                  users={users}
                  onRowClick={setSelectedUser}
                  activePlansOf={activePlansOf}
                  sortAndFilterControls={{
                    sort: tableSort,
                    onSortChange: changeTableSort,
                    filters: tableFilters,
                    onFiltersChange: changeTableFilters,
                  }}
                />

                <div className={"mt-4 flex flex-col items-center gap-2"}>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onChangePage={toPage}
                  />
                  <span className={"text-sm text-slate-500"}>
                    Page {currentPage + 1} of {totalPages}
                  </span>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
    </Layout>
  );
}
